import { Alert, Box, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import QrScanner from "qr-scanner";
import { useStaffOverrideQrScanner } from "../dialog/staffOverrideCameraDialog/hooks/useStaffOverrideQrScanner/useStaffOverrideQrScanner";
import { StaffScannerProps } from "../../qr/interfaces";
import { StaffScannerModal } from "../../freya/scanSuccess/ScanSuccess";
import { BadgePhoto } from "./badgePhoto/BadgePhoto";
import useStyles from "./styles/styles";
import PublicApiService from "../../../services/PublicApiService";
import { BadgePaymentStatus, FreyaScanningMode } from "../../../common/enums";
import { IPersonBadge } from "../../../common/types";
import { useScannerInput } from "../../freya/scanBadge/scannerInput/hooks/useScannerInput";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { SNACKBAR_VARIANTS, STAFF_OVERRIDE_CAMERA_KEY } from "../../../common/constants";
import { QrTypes } from "../../qr/enums";
import { CameraModeContent } from "./cameraModeContent/CameraModeContent";
import localForage from "localforage";

type Props = {
  onSuccess: () => void;
  alertMessage?: string;
  options?: StaffScannerModal;
  badges: IPersonBadge[];
  reason?: string;
  onAuthorized?: (qrData: string) => void;
} & StaffScannerProps;

const QR_TIMEOUT = 1000;

const styles = {
  qrContainer: (loading: boolean) => {
    return { opacity: loading ? 0.25 : 1 };
  }
};

export const StaffOverrideCamera = ({
  onSuccess,
  alertMessage,
  successOnMount,
  isFreya,
  skipUserAccessCheck,
  badges,
  options,
  reason,
  allowedRoles,
  onAuthorized
}: Props) => {
  const classes = useStyles();
  const { checkQrCodeForOverride } = useStaffOverrideQrScanner({ isFreya, skipUserAccessCheck, allowedRoles });
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("common");
  const [scanStatus, setScanStatus] = useState(QrTypes.SCANNING);
  const [scanMode, setScanMode] = useState<FreyaScanningMode>(FreyaScanningMode.SCANNER);

  const validateQrCode = async (result: QrScanner.ScanResult, skipValidation?: boolean) => {
    try {
      const { success, authorizedById } = await checkQrCodeForOverride(result.data);
      if (success || skipValidation) {
        for (let badge of badges) {
          if (!skipUserAccessCheck && badge?.paymentStatus === BadgePaymentStatus.UNPAID) {
            await PublicApiService.updatePaymentStatus(badge.id, BadgePaymentStatus.UNPAID_OVERRIDDEN, authorizedById, reason);
          }
        }
        setScanStatus(QrTypes.SUCCESS);
        setTimeout(() => {
          onSuccess();
        }, QR_TIMEOUT);
        if (onAuthorized) {
          onAuthorized(result?.data ? result.data.trim() : "");
        }
      } else {
        setScanStatus(QrTypes.ERROR);
        setTimeout(() => setScanStatus(QrTypes.SCANNING), QR_TIMEOUT);
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar(<b>{t("errors.genericClientError")}</b>, SNACKBAR_VARIANTS.error);
    }
  };

  const defineScannerMode = async () => {
    const cachedValue = await localForage.getItem(STAFF_OVERRIDE_CAMERA_KEY);

    if (!cachedValue || cachedValue === FreyaScanningMode.SCANNER) {
      setScanMode(FreyaScanningMode.SCANNER);
      return;
    }
    setScanMode(FreyaScanningMode.CAMERA);
  };

  useEffect(() => {
    defineScannerMode();
    if (successOnMount) {
      validateQrCode({ data: "", cornerPoints: [] }, true);
    }
  }, []);

  const { onScannerInputChange, scannerLoading } = useScannerInput({ validateQrCode, scanStatus });

  if (options?.isStaffScanningSuccess) {
    setTimeout(() => {
      setLoading(false);
    }, 1500);
  }

  return (
    <>
      {alertMessage && (
        <Alert severity="warning" sx={{ marginBottom: 2, fontWeight: "500" }}>
          {alertMessage}
        </Alert>
      )}
      {options?.isStaffScanningSuccess ? (
        <Box className={classes.scanningResultContainer}>
          <BadgePhoto loading={loading} photoUrl={options?.photoUrl || ""} />
        </Box>
      ) : (
        <div className={classes.scannerContainer}>
          {scannerLoading && (
            <div className={classes.loaderContainer}>
              <CircularProgress />
            </div>
          )}
          <Box sx={styles.qrContainer(scannerLoading)} className={classes.contentContainer}>
            <CameraModeContent
              scannerMode={scanMode === FreyaScanningMode.SCANNER}
              scanStatus={scanStatus}
              onScan={validateQrCode}
              scannerProps={{ onScannerInputChange }}
            />
            <Box
              sx={{
                marginBottom: "15px",
                fontSize: "20px"
              }}
            >
              {t("search.staffOverrideAccessDialog.scanYourQrCode")}
            </Box>
          </Box>
        </div>
      )}
    </>
  );
};
